import { z } from "zod";
import { IntakeSchema } from "./preparation-card.js";
import { AiDraftSchema, validateDraft } from "./ai-intake.js";

export const PasswordSchema = z.string().min(15).max(128);
export const NameSchema = z.string().trim().min(1).max(40);
export const KoreanPhoneSchema = z.string().regex(/^\+8210\d{8}$/u);
export const AuthCapabilitiesSchema = z.object({ email: z.boolean(), phone: z.boolean(), kakao: z.boolean(), naver: z.boolean() });
export type AuthCapabilities = z.infer<typeof AuthCapabilitiesSchema>;
export const RecordContentSchema = z.object({
  intake: IntakeSchema.strict(), ageUnit: z.enum(["YEARS", "MONTHS"]), aiDraft: AiDraftSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (value.ageUnit === "YEARS" && value.intake.ageMonths !== null && value.intake.ageMonths % 12 !== 0) context.addIssue({ code: "custom", message: "INVALID_AGE_UNIT" });
  if (value.aiDraft !== null) {
    try { validateDraft(value.aiDraft, value.intake.situation); } catch { context.addIssue({ code: "custom", message: "INVALID_AI_DRAFT" }); }
    if (Object.values(value.intake.redFlags).includes("YES")) context.addIssue({ code: "custom", message: "AI_NOT_APPLICABLE" });
  }
});
export const SaveRecordSchema = z.object({ requestId: z.uuid(), storageConsent: z.literal(true), content: RecordContentSchema }).strict();
export const AccountUserSchema = z.object({ id: z.string().min(1).max(128), name: NameSchema }).strict();
export const RecordMetaSchema = z.object({ id: z.uuid(), createdAt: z.iso.datetime() }).strict();
export const SavedRecordSchema = RecordMetaSchema.extend({ content: RecordContentSchema }).strict();
export type AccountUser = z.infer<typeof AccountUserSchema>;
export type RecordContent = z.infer<typeof RecordContentSchema>;
export type RecordMeta = z.infer<typeof RecordMetaSchema>;
export type SavedRecord = z.infer<typeof SavedRecordSchema>;
export const RECORD_LIMIT = 100;
export const SESSION_SECONDS = 12 * 60 * 60;

export class AccountError extends Error {
  constructor(public readonly code: "INVALID_CREDENTIALS" | "ACCOUNT_UNAVAILABLE" | "AUTH_REQUIRED" | "TRY_LATER" | "RECORD_NOT_FOUND" | "RECORD_LIMIT" | "REQUEST_CONFLICT") { super(code); }
}
export interface RecordStore {
  list(user: AccountUser): RecordMeta[];
  get(user: AccountUser, id: string): SavedRecord;
  save(user: AccountUser, requestId: string, content: RecordContent): RecordMeta;
  deleteRecord(user: AccountUser, id: string): void;
  deleteAll(userId: string): void;
}
export interface AccountAuth {
  capabilities: AuthCapabilities;
  handler(request: Request): Promise<Response>;
  session(headers: Headers): Promise<AccountUser | null>;
  finishPhoneSignup(headers: Headers, name: string, password: string): Promise<void>;
}
